import React, { useState, useEffect } from 'react';
import {
  Pagination,
  CircularProgress,
  TextField
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Usuarios.css';
import EditarUsuario from './EditarUsuario';
import avatar from '../recursos/avatar.png';
import NuevoCliente from '../NuevoCliente';
import Fuse from 'fuse.js';

const Usuarios = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002';

  const [openNuevoCliente, setOpenNuevoCliente] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [clienteEdit, setClienteEdit] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    telefono: '',
    observaciones: ''
  });

  const itemsPerPage = 50;

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/clientes`);
        if (!response.ok) throw new Error('Error al obtener clientes');
        const data = await response.json();
        // Agregar campo virtual para búsqueda
        const dataConNombreCompleto = data.map(cliente => ({
          ...cliente,
          nombreCompleto: `${cliente.nombre || ''} ${cliente.apellido || ''} ${cliente.telefono || ''}`.toLowerCase()
        }));
        setClientes(dataConNombreCompleto);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchClientes();
  }, [apiUrl]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const fuse = new Fuse(clientes, {
    keys: ['nombreCompleto'],
    threshold: 0.3,
    ignoreLocation: true,
  });

  const filteredClientes = searchTerm.trim()
    ? fuse.search(searchTerm.toLowerCase()).map(result => result.item)
    : [...clientes];

  const sortedClientes = filteredClientes.sort((a, b) => {
    if (!sortColumn) return 0;
    const aVal = a[sortColumn]?.toString().toLowerCase() ?? '';
    const bVal = b[sortColumn]?.toString().toLowerCase() ?? '';
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedClientes = sortedClientes.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleOpenModal = (cliente) => {
    setClienteEdit(cliente);
    setFormData({
      nombre: cliente.nombre,
      apellido: cliente.apellido,
      telefono: cliente.telefono || '',
      observaciones: cliente.observaciones || ''
    });
    setOpenModal(true);
  };

  const handleCloseNuevoCliente = () => {
    setOpenNuevoCliente(false);
  };

  const handleClienteCreado = (nuevoCliente) => {
    const clienteConNombreCompleto = {
      ...nuevoCliente,
      nombreCompleto: `${nuevoCliente.nombre || ''} ${nuevoCliente.apellido || ''} ${nuevoCliente.telefono || ''}`.toLowerCase()
    };
    setClientes([clienteConNombreCompleto, ...clientes]);
    setOpenNuevoCliente(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatFecha = (fechaString) => {
    if (!fechaString) return 'N/A';
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    try {
      const fecha = new Date(fechaString);
      const dia = String(fecha.getDate()).padStart(2, '0');
      const mes = meses[fecha.getMonth()];
      const año = fecha.getFullYear();
      return `${dia}-${mes}-${año}`;
    } catch (e) {
      console.error('Error formateando fecha:', e);
      return 'N/A';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clienteActualizado = {
      nombre_cliente: formData.nombre,
      apellidos_cliente: formData.apellido,
      telefono: formData.telefono || null,
      observaciones: formData.observaciones || null
    };

    try {
      const response = await fetch(`${apiUrl}/clientes/${clienteEdit.id_cliente}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clienteActualizado)
      });

      if (!response.ok) throw new Error('Error al actualizar cliente');

      const updatedClientes = clientes.map(cliente =>
        cliente.id_cliente === clienteEdit.id_cliente
          ? {
              ...cliente,
              ...clienteActualizado,
              nombreCompleto: `${formData.nombre} ${formData.apellido} ${formData.telefono}`.toLowerCase()
            }
          : cliente
      );

      setClientes(updatedClientes);
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger text-center">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="container-fluid usuarios-container">
      <div className="row justify-content-center">
        <div className="col-md-11 contenedor-usuarios">
          <div className='contenedor-encabezado'>
            <div className="row mb-3 align-items-center">
              <div className="col-md-6">
                <button className="btn btn-primary btn-agregar-usuario" onClick={() => setOpenNuevoCliente(true)}>
                  + Usuario
                </button>
              </div>
              <div className="col-md-6 text-end">
                <h2 className="titulo-lista">Lista de Clientes</h2>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-12">
                <div className="contenedor-buscador">
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={handleSearch}
                    fullWidth
                    className="campo-busqueda"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon className="icono-busqueda" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='contenedor-tabla'>
            <div className="table-container">
              <table className="table tabla-usuarios">
                <thead>
                  <tr>
                    <th onClick={() => setSortColumn('id_cliente')} style={{ cursor: 'pointer' }}>
                      ID Usuario{sortColumn === 'id_cliente' ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                    <th onClick={() => setSortColumn('nombre')} style={{ cursor: 'pointer' }}>
                      Nombre{sortColumn === 'nombre' ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                    <th onClick={() => setSortColumn('telefono')} style={{ cursor: 'pointer' }}>
                      Teléfono{sortColumn === 'telefono' ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                    <th onClick={() => setSortColumn('fecha_alta')} style={{ cursor: 'pointer' }}>
                      Fecha Alta{sortColumn === 'fecha_alta' ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                    <th>
                      Última Cita
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClientes.map(cliente => (
                    <tr
                      key={cliente.id_cliente}
                      onClick={() => handleOpenModal(cliente)}
                      className="fila-usuario"
                    >
                      <td>{cliente.id_cliente}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="avatar-container me-3">
                            <img src={avatar} alt="Avatar" className="avatar-img" />
                          </div>
                          <div>{cliente.nombre} {cliente.apellido}</div>
                        </div>
                      </td>
                      <td>{cliente.telefono || 'N/A'}</td>
                      <td>{formatFecha(cliente.fecha_alta)}</td>
                      <td>{formatFecha(cliente.ultima_cita)}</td> {/* Agregar columna de última cita */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="contenedor-paginacion">
              <Pagination
                count={Math.ceil(filteredClientes.length / itemsPerPage)}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </div>
          </div>

          <EditarUsuario
            open={openModal}
            onClose={handleCloseModal}
            formData={formData}
            onFormChange={handleChange}
            onSubmit={handleSubmit}
          />

          <NuevoCliente
            open={openNuevoCliente}
            onClose={handleCloseNuevoCliente}
            onClienteCreado={handleClienteCreado}
          />
        </div>
      </div>
    </div>
  );
};

export default Usuarios;
